import { Link, NavLink } from "react-router-dom";
import { LogOut } from "lucide-react";
import { MENU_ITEMS } from "@/config/menu";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useRuntimeEnv } from "@/lib/runtimeEnv";

// Shows only what the server confirms (PROD, VORSCHAU, TEST, DEV); without confirmation "Umgebung unbekannt".
function EnvLabel() {
  const { t } = useI18n();
  const { status, labelKey } = useRuntimeEnv();
  if (status === "loading") return null;
  return <> · <span data-testid="sidebar-env-label" data-env-status={status}
    className={status === "unknown" ? undefined : "font-semibold text-brand-magenta-light"}>{t(labelKey)}</span></>;
}

// C120: a server-provided nav_menu (organisation-bound accounts) replaces the full menu; help is otherwise in the footer.
function visibleItems(user) {
  if (user?.nav_menu) return MENU_ITEMS.filter((item) => user.nav_menu.includes(item.key));
  const isSuper = user?.permissions?.includes("*");
  const allowed = user?.allowed_menu || [];
  return MENU_ITEMS.filter((item) => item.key !== "help" && (isSuper || allowed.includes(item.key)));
}

function NavItem({ item, onNavigate }) {
  const { t } = useI18n();
  const Icon = item.icon;
  return (
    <NavLink to={item.path} end={item.path === "/"} onClick={onNavigate} data-testid={`nav-${item.key}`}
      className={({ isActive }) => `group flex min-h-[48px] items-center gap-3 rounded-lg px-3 py-2 text-lg font-medium transition-colors duration-150 ${
        isActive ? "bg-brand-magenta text-white shadow-sm" : "text-purple-100/85 hover:bg-white/10 hover:text-white"}`}>
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">{t(`nav.${item.key}`)}</span>
    </NavLink>
  );
}

export function SidebarNav({ onNavigate, onLogout }) {
  const { user } = useAuth();
  const { t } = useI18n();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-4 pb-4 pt-5">
        <img src="/visibilis-wordmark-light.png" alt="Visibilis.life – Menschen mit Behinderungen" className="h-9 w-auto" data-testid="sidebar-wordmark" />
      </div>
      <p className="px-4 pb-2 text-xs font-semibold text-purple-100" aria-hidden="true">Control OS</p>
      <nav aria-label="Hauptnavigation" className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-6">
        {visibleItems(user).map((item) => <NavItem key={item.key} item={item} onNavigate={onNavigate} />)}
      </nav>
      <div className="space-y-1 border-t border-white/10 px-4 py-4">
        {onLogout && (
          <button type="button" onClick={onLogout} data-testid="mobile-logout-button"
            className="mb-2 flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 text-sm font-semibold text-white hover:bg-white/10">
            <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />Abmelden
          </button>
        )}
        {!user?.nav_menu && <Link to="/hilfe" onClick={onNavigate} data-testid="sidebar-help-link" className="flex min-h-[48px] items-center rounded-lg text-base text-purple-100 underline underline-offset-4">Hilfe bleibt erreichbar</Link>}
        <p className="text-xs text-purple-200/70">{t("app.name")}<EnvLabel /></p>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside className="fixed inset-y-0 left-0 hidden w-60 bg-brand-purple lg:block" aria-label="Seitennavigation">
      <SidebarNav />
    </aside>
  );
}
